import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, GitMerge, ArrowRight } from "lucide-react";

const COLORS = {
  added: "#0A7029",
  removed: "#C50F1F",
  changed: "#002FA7",
};

export default function ScenarioDiffDialog({ scenarioId, open, onOpenChange }) {
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    if (open && scenarioId) {
      api.get(`/scenarios/${scenarioId}/diff`).then((r) => setDiff(r.data)).catch(() => setDiff(null));
    }
  }, [open, scenarioId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-none" data-testid="scenario-diff-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: '"Work Sans", sans-serif' }}>
            Scenario diff{diff?.name ? ` — ${diff.name}` : ""}
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-600">
            Side-by-side comparison of the scenario against the live org chart. Merging applies every line below.
          </DialogDescription>
        </DialogHeader>

        {!diff ? (
          <p className="px-2 py-8 text-center text-sm text-neutral-500">Loading…</p>
        ) : diff.total_changes === 0 ? (
          <div className="border border-dashed border-neutral-300 bg-neutral-50 px-6 py-10 text-center">
            <p className="text-sm text-neutral-600">No changes yet. Open the scenario in the Organogram, edit an employee, and come back.</p>
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-6 overflow-y-auto">
            {/* Added */}
            {diff.added.length > 0 && (
              <Section title="Added" count={diff.added.length} Icon={Plus} color={COLORS.added}>
                <ul>
                  {diff.added.map((a) => (
                    <li key={a.id} className="border-b border-neutral-100 px-3 py-2 last:border-0" data-testid={`diff-added-${a.id}`}>
                      <p className="text-sm font-medium">{a.name}</p>
                      <p className="text-xs text-neutral-600">{a.designation || "—"} · {a.department || "—"}</p>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Changed */}
            {diff.changed.length > 0 && (
              <Section title="Changed" count={diff.changed.length} Icon={Pencil} color={COLORS.changed}>
                <ul>
                  {diff.changed.map((c) => (
                    <li key={c.id} className="border-b border-neutral-100 px-3 py-3 last:border-0" data-testid={`diff-changed-${c.id}`}>
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="mb-2 text-[10px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        {c.designation || "—"}
                      </p>
                      <ul className="space-y-1">
                        {c.fields.map((f, i) => (
                          <li key={i} className="flex items-start gap-3 text-xs">
                            <span className="w-24 font-mono text-[10px] uppercase tracking-wider text-neutral-500">
                              {f.field}
                            </span>
                            <span className="line-through text-neutral-500">{stringify(f.before)}</span>
                            <ArrowRight size={11} className="mt-0.5 shrink-0 text-neutral-400"/>
                            <span className="text-[#002FA7]">{stringify(f.after)}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Removed */}
            {diff.removed.length > 0 && (
              <Section title="Removed" count={diff.removed.length} Icon={Trash2} color={COLORS.removed}>
                <ul>
                  {diff.removed.map((r) => (
                    <li key={r.id} className="border-b border-neutral-100 px-3 py-2 last:border-0 line-through text-neutral-500" data-testid={`diff-removed-${r.id}`}>
                      <p className="text-sm">{r.name}</p>
                      <p className="text-xs">{r.designation || "—"}</p>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function stringify(v) {
  if (v == null || v === "") return <span className="italic text-neutral-400">empty</span>;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function Section({ title, count, Icon, color, children }) {
  return (
    <div className="border border-neutral-200">
      <div className="flex items-center gap-2 border-b border-neutral-200 px-3 py-2" style={{ background: `${color}10` }}>
        <span className="flex h-5 w-5 items-center justify-center" style={{ background: color }}>
          <Icon size={11} className="text-white"/>
        </span>
        <h3 className="text-xs font-medium uppercase tracking-wider" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          {title}
        </h3>
        <Badge variant="outline" className="ml-auto rounded-none border-neutral-300 text-[10px]">
          {count}
        </Badge>
      </div>
      <div>{children}</div>
    </div>
  );
}
